import { getApiErrorStatus } from '~/common/errors/getApiErrorStatus';
import { RbacAction, RbacResource } from '~/types/Rbac';
import { captureException } from '~/utils/sentry';

import { ALL_RBAC_PERMISSIONS, RBAC_ACTIONS } from './allRbacPermissions';
import { checkPermissionsBulk } from './checkPermissionsBulk';

export type AllPermissions = Record<RbacResource, Record<RbacAction, boolean>>;

/**
 * Resolves every RBAC pair the app can ask about. This is the only permission
 * request the app makes: `usePermission` and `usePermissions` select out of
 * this single cache entry rather than each fetching their own pair.
 *
 * Sent as one request per action, in parallel, because pilot rejects a
 * `/permission/bulk/` payload of more than **50 checks** — and the full cross
 * product is already 58. Splitting by action puts one resource-list per
 * request, which keeps each at 29 today and is the natural seam: `view` and
 * `edit` grow together, so the two stay balanced as resources are added.
 *
 * This buys headroom, it does not remove the ceiling. Past 50 resources a
 * single action's request breaks the limit again and this needs real chunking.
 *
 * `clientSlug` is not sent — the session identifies the tenant. It is the cache
 * key, so that `enterClientSlug` crossing tenants in place (no page load) lands
 * on a new entry and refetches instead of serving the previous tenant's
 * answers.
 */
export async function checkAllPermissions(
  _clientSlug: string,
): Promise<AllPermissions> {
  // `createQueryFn` turns a rejection into RTK's error state and reports
  // nothing, so a permission check that never completes is invisible in
  // Sentry — which is how EKP-63152 reached an escalation with no matching
  // issue. Captured here rather than at a call site so a failure is recorded
  // once per request, whether or not anything is reading it yet.
  let responses;
  try {
    responses = await Promise.all(
      RBAC_ACTIONS.map((action) =>
        checkPermissionsBulk(
          ALL_RBAC_PERMISSIONS.filter(
            ({ resourceType }) => resourceType === action,
          ),
        ),
      ),
    );
  } catch (error: unknown) {
    captureException('Permission check failed', error, {
      reason: 'check_all_permissions_failed',
      status: String(getApiErrorStatus(error) ?? 'none'),
    });
    throw error;
  }

  const results: Record<string, Record<string, boolean>> = {};
  for (const response of responses) {
    for (const [resourceId, actions] of Object.entries(response)) {
      results[resourceId] = { ...results[resourceId], ...actions };
    }
  }

  // Normalized so every pair is present: a resource the server omits reads as
  // an explicit deny rather than `undefined` at the call site.
  const permissions = {} as AllPermissions;
  for (const { resourceId, resourceType } of ALL_RBAC_PERMISSIONS) {
    permissions[resourceId] = {
      ...permissions[resourceId],
      [resourceType]: results[resourceId]?.[resourceType] ?? false,
    } as Record<RbacAction, boolean>;
  }
  return permissions;
}
